import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { useSeo, breadcrumbJsonLd } from "../lib/seo";
import { CTABlock } from "./ServicesPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    axios
      .get(`${API}/insights/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);
  useSeo({
    title: post ? post.meta_title || post.title : "Insight",
    description: post ? post.meta_description || post.excerpt : "",
    path: `/insights/${slug}`,
    image: post?.og_image,
    jsonLd: post && [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        author: { "@type": "Person", name: post.author },
        datePublished: post.published_at || post.created_at,
        dateModified: post.updated_at,
      },
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Insights", path: "/insights" },
        { name: post.title, path: `/insights/${slug}` },
      ]),
    ],
  });
  if (notFound) {
    return (
      <SiteLayout>
        <div className="container-wald py-24">
          <div className="eyebrow mb-4">404</div>
          <h1 className="font-display text-3xl mb-4">Insight not found.</h1>
          <Link to="/insights" className="copper-link">Back to insights →</Link>
        </div>
      </SiteLayout>
    );
  }
  if (!post) {
    return (
      <SiteLayout>
        <div className="container-wald py-24 opacity-60">Loading…</div>
      </SiteLayout>
    );
  }
  return (
    <SiteLayout>
      <section className="border-b border-border">
        <div className="container-wald py-12">
          <Link to="/insights" data-testid="insight-back" className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100">
            <ArrowLeft size={14} strokeWidth={2} /> All insights
          </Link>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-16 md:py-24 max-w-3xl">
          <div className="eyebrow mb-6">{post.category}</div>
          <h1 className="font-display text-3xl md:text-5xl leading-[1.1] tracking-tight">{post.title}</h1>
          <div className="mt-8 flex flex-wrap gap-4 text-xs opacity-60 font-mono-tab">
            <span>{post.author}</span>
            <span>·</span>
            <span>{(post.published_at || post.created_at || "").slice(0, 10)}</span>
            {(post.tags || []).map((t) => (
              <span key={t} className="uppercase tracking-wider">#{t}</span>
            ))}
          </div>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-16 max-w-3xl">
          <div className="prose-wald">
            {post.body.split(/\n\n+/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </section>
      <CTABlock />
    </SiteLayout>
  );
}
